import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import useOnClickOutside from 'use-onclickoutside';
import { ButtonWithDropdown } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import useGridNavigation from './useGridNavigation.js';

const PrescriptionDropdown = ({ options , onChange , disabled , leftColumnOptions , title , placeholder , rightColumnOptions , className  })=>{
    const isDisabled = disabled || options.length <= 1;
    const grid = [];
    if (leftColumnOptions.length > 0) {
        grid.push(leftColumnOptions.length);
    }
    if (rightColumnOptions.length > 0) {
        grid.push(rightColumnOptions.length);
    }
    const rowCount = grid.reduce((prev, curr)=>prev > curr ? prev : curr
    , 0);
    const dropdownRef = React.useRef(null);
    const gridRef = React.useRef(null);
    const [value1, setValue] = React.useState(placeholder || '');
    const [isOpen, setIsOpen] = React.useState(false);
    const [{ rowIndex: rowIndex1 , colIndex: colIndex1  }, setGridFocus] = useGridNavigation(isOpen, grid, dropdownRef, ()=>setIsOpen(false)
    );
    useOnClickOutside(dropdownRef, ()=>setIsOpen(false)
    );
    const handleKeyDown = (event)=>{
        if (event.key === 'Escape') {
            var ref, ref1;
            event.stopPropagation();
            setIsOpen(false);
            (ref1 = (ref = dropdownRef.current) === null || ref === void 0 ? void 0 : ref.querySelector('button')) === null || ref1 === void 0 ? void 0 : ref1.focus();
        }
    };
    React.useEffect(()=>{
        const dropdownNode = dropdownRef.current;
        if (isOpen && dropdownNode) {
            dropdownNode.addEventListener('keydown', handleKeyDown);
            return ()=>{
                dropdownNode.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [
        isOpen
    ]);
    /**
   * Effect that adds focus to a selected element,
   * Focus is changed after navigating with the keyboard controls,
   * on mouse hover or whenever the drodpown is opened
   */ React.useEffect(()=>{
        const elementRef = gridRef && gridRef.current;
        const selectedRow = elementRef && elementRef.querySelector(`.prescription-dropdown__grid-row-${rowIndex1}`);
        const cellIndex = grid.length === 1 ? leftColumnOptions.length > 0 ? 0 : 1 : colIndex1;
        const selectedChild = selectedRow && selectedRow.childNodes[cellIndex];
        if (selectedRow && selectedChild) {
            selectedChild.focus();
        }
    }, [
        rowIndex1,
        colIndex1,
        isOpen,
        grid,
        leftColumnOptions
    ]);
    /**
   * When only one option is given,
   * pre-select it
   */ React.useEffect(()=>{
        if (options.length === 1) {
            selectOption(options[0], 0, 0);
        }
    }, [
        options.length
    ]);
    const dropdownClass = classnames('prescription-dropdown', className, {
        'prescription-dropdown--disabled': isDisabled
    });
    const columnHeaderClass = classnames('prescription-dropdown__grid-cell', 'prescription-dropdown__grid-header', 'prescription-dropdown__grid-cell--disabled');
    const buttonWithDropdownClass = classnames('prescription-dropdown__button', {
        'prescription-dropdown__button--disabled': isDisabled
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: dropdownClass,
        ref: dropdownRef
    }, /*#__PURE__*/ React.createElement(ButtonWithDropdown, {
        isActive: isOpen,
        label: renderLabel(value1 || placeholder),
        title: title,
        onClick: ()=>!isDisabled && setIsOpen(!isOpen)
        ,
        className: buttonWithDropdownClass
    }, /*#__PURE__*/ React.createElement("div", {
        className: "prescription-dropdown__container"
    }, /*#__PURE__*/ React.createElement("div", {
        role: "grid",
        className: "prescription-dropdown__grid",
        "aria-label": title,
        ref: gridRef
    }, /*#__PURE__*/ React.createElement("div", {
        className: "prescription-dropdown__grid-row",
        role: "row"
    }, leftColumnOptions.length > 0 && /*#__PURE__*/ React.createElement("div", {
        "aria-label": "-",
        role: "columnheader",
        className: columnHeaderClass
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "minus",
        className: "prescription-dropdown__icon"
    })), rightColumnOptions.length > 0 && /*#__PURE__*/ React.createElement("div", {
        "aria-label": "+",
        role: "columnheader",
        className: columnHeaderClass
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "plus",
        className: "prescription-dropdown__icon"
    }))), renderGrid()))));
    function renderGrid() {
        const rows = [];
        for(let i = 0; i < rowCount; i++){
            const left = leftColumnOptions[i];
            const right = rightColumnOptions[i];
            const row = /*#__PURE__*/ React.createElement("div", {
                className: `prescription-dropdown__grid-row prescription-dropdown__grid-row-${i}`,
                role: "row",
                key: i
            }, left ? renderCell(left, i, 0) : renderEmptyCell(), right ? renderCell(right, i, 1) : renderEmptyCell());
            rows.push(row);
        }
        return rows;
    }
    function renderEmptyCell() {
        const emptyCellClass = classnames('prescription-dropdown__grid-cell', 'prescription-dropdown__grid-cell--disabled');
        return /*#__PURE__*/ React.createElement("div", {
            role: "gridcell",
            className: emptyCellClass
        });
    }
    function renderCell(option, rowIndex = 0, colIndex = 0) {
        const gridCellClass = classnames('prescription-dropdown__grid-cell', {
            'prescription-dropdown__grid-cell--single-column': grid.length === 1
        });
        return /*#__PURE__*/ React.createElement("button", {
            role: "gridcell",
            className: gridCellClass,
            onClick: ()=>selectOption(option, rowIndex, colIndex)
            ,
            onMouseEnter: ()=>setGridFocus(rowIndex, colIndex)
        }, option.value);
    }
    function selectOption(option, rowIndex, colIndex) {
        setValue(option.value);
        setGridFocus(rowIndex, colIndex);
        setIsOpen(false);
        const validatedSelection = swcHelpers.objectSpread({}, option, {
            isValid: option.value !== ''
        });
        onChange && onChange(validatedSelection);
    }
    function renderLabel(value) {
        if (!value) {
            return null;
        }
        const valueClass = classnames('prescription-dropdown__value', {
            'prescription-dropdown__value--invalid': !value || value === placeholder,
            'prescription-dropdown__value--disabled': options.length <= 1
        });
        return /*#__PURE__*/ React.createElement("span", {
            className: "prescription-dropdown__button-label"
        }, /*#__PURE__*/ React.createElement("span", {
            className: valueClass,
            "data-t": "prescription-dropdown-value"
        }, value));
    }
};
var PrescriptionDropdown$1 = PrescriptionDropdown;

export { PrescriptionDropdown, PrescriptionDropdown$1 as default };
//# sourceMappingURL=PrescriptionDropdown.js.map
